import { faEdit } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMemo } from 'react'
import styled from 'styled-components'

import {
	Button,
	Container,
	DialogWrapper,
	LightTable,
	Loader,
	Message,
	MessageModal,
} from '@/components'
import { Flex } from '@/components/Layout'
import { getEnvironmentsWithLastDeploymentPerObjectType } from '@/endpoints'
import { useApi } from '@/endpoints/hooks'
import {
	EnvironmentDeploymentDto,
	ObjectSettingsDto,
	ReleaseDto,
} from '@/endpoints/models'
import { useAppContext } from '@/hooks'
import { StructureDto } from '@/rtkApi/mddApiSliceGenerated'
import { LightTableColumn } from '@/types'

import { DdlModal } from './components/DdlModal'
import { useReleaseRedirect } from './hooks'

type DeploymentsProps = { editMode: boolean; node: StructureDto }

export const Deployments = ({ node, editMode }: DeploymentsProps) => {
	const { t } = useAppContext()

	const environments = useApi(
		getEnvironmentsWithLastDeploymentPerObjectType(node.id as number),
	)

	const onReleaseRedirect = useReleaseRedirect(node)

	const model = useMemo(
		() =>
			[
				{
					field: 'objectType',
					title: t('OBJECT_TYPE'),
					width: 50,
					formatter: (v?: ObjectSettingsDto) => v?.name,
				},
				{
					field: 'name',
					title: t('ENVIRONMENT'),
					width: 50,
				},
				{
					field: 'lastDeploymentStructureHistoryVersion',
					title: t('DEPLOYED_VERSION'),
					width: 50,
					formatter: (v) =>
						v ? t('DEPLOYED_VERSION_INFO', [v as string]) : 'N/A',
				},
				{
					field: 'lastDeploymentAuthorCompositeName',
					title: t('DEPLOYED_BY'),
					width: 50,
					formatter: (v) => (v ? v : 'N/A'),
				},
				{
					field: 'lastRelease',
					title: t('DEPLOYMENT_PACKAGE'),
					width: 50,
					formatter: (v?: ReleaseDto) =>
						v?.name ? (
							<ReleaseRedirect
								data-testid="deployments-package-link"
								onClick={() => onReleaseRedirect(v)}
							>
								<FontAwesomeIcon icon={faEdit} className="mr-1" />
								{v.name}
							</ReleaseRedirect>
						) : (
							'N/A'
						),
				},
				{
					field: null,
					key: 'actions',
					width: 200,
					maxWidth: 200,
					formatter: (_, item) => (
						<Actions>
							<DialogWrapper
								dialog={(opened, onClose) =>
									opened && (
										<>
											<DdlModal
												preview
												environment={item}
												node={node}
												onClose={onClose}
											/>
										</>
									)
								}
							>
								{(onClick) => (
									<Button size="sm" onClick={onClick}>
										{t('PREVIEW_DLL')}
									</Button>
								)}
							</DialogWrapper>
							<DialogWrapper
								dialog={(opened, onClose) =>
									opened && (
										<>
											<DdlModal
												environment={item}
												node={node}
												onClose={onClose}
											/>
											{editMode && (
												<MessageModal
													title={t('DEPLOYED_UNPUSHED_CHANGES_WARN_TITLE')}
													type="warn"
													message={t('DEPLOYED_UNPUSHED_CHANGES_WARN')}
												/>
											)}
										</>
									)
								}
							>
								{(onClick) => (
									<Button size="sm" onClick={onClick}>
										{t('CREATE_DDL')}
									</Button>
								)}
							</DialogWrapper>
						</Actions>
					),
				},
			] as LightTableColumn<EnvironmentDeploymentDto>[],
		[t, onReleaseRedirect, node, editMode],
	)

	return (
		<Container>
			{environments.loading ? (
				<Loader loaded={false} />
			) : (
				<>
					{editMode && (
						<Message
							type="warn"
							message={t('DEPLOYED_UNPUSHED_CHANGES_WARN')}
						/>
					)}

					<LightTable
						keyField="key"
						isLoading={environments.loading}
						data={environments.data || []}
						columns={model}
					/>
				</>
			)}
		</Container>
	)
}

const Actions = styled(Flex)`
	button {
		margin-right: 10px;
	}
`

const ReleaseRedirect = styled(Flex)`
	cursor: pointer;
`
